import { useEffect, useState } from 'react';
import { staffApi, formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const PAYMENT_METHODS = ['CASH', 'CARD', 'INTERAC', 'E_TRANSFER'];

// Staff/POS adds an add-on mid-plan, in-store. Priced PER REMAINING TIFFIN and charged
// immediately as a paid in-store payment. Does not change the customer's tiffin credits.
export function AddOnDialog({ open, onOpenChange, subscriptionId, remainingCredits, customerName, onDone }) {
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState([]);
  const [method, setMethod] = useState('CASH');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected([]);
    setReferenceNumber('');
    setNotes('');
    staffApi.get('/items').then(({ data }) => setItems(data.items || []));
  }, [open]);

  const toggle = (id) => setSelected((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));

  const total = (items || [])
    .filter((it) => selected.includes(it.id))
    .reduce((s, it) => s + Number(it.price) * remainingCredits, 0);

  const confirm = async () => {
    if (!selected.length) return;
    setBusy(true);
    try {
      await staffApi.post(`/subscriptions/${subscriptionId}/addons`, { itemIds: selected, method, referenceNumber, notes });
      toast.success('Add-on added and charged in-store');
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="staff-addon-dialog">
        <DialogHeader>
          <DialogTitle>Add Add-on for {customerName}</DialogTitle>
          <DialogDescription>
            Priced per remaining tiffin ({remainingCredits} left) and charged in-store now. Tiffin balance is unchanged.
          </DialogDescription>
        </DialogHeader>

        {items === null ? (
          <p className="text-sm text-muted-ink">Loading add-ons…</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-ink" data-testid="staff-addon-empty">No active add-ons available.</p>
        ) : (
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Add-ons</Label>
              {items.map((it) => (
                <label
                  key={it.id}
                  data-testid={`staff-addon-option-${it.id}`}
                  className="flex items-center justify-between gap-3 rounded-lg border border-border/50 p-2.5 cursor-pointer"
                >
                  <span className="flex items-center gap-2.5">
                    <Checkbox
                      data-testid={`staff-addon-checkbox-${it.id}`}
                      checked={selected.includes(it.id)}
                      onCheckedChange={() => toggle(it.id)}
                    />
                    <span className="text-sm text-charcoal">{it.name}</span>
                  </span>
                  <span className="text-xs text-muted-ink">
                    ${Number(it.price).toFixed(2)}/tiffin → ${(Number(it.price) * remainingCredits).toFixed(2)}
                  </span>
                </label>
              ))}
            </div>

            <div className="space-y-1.5">
              <Label>Payment Method</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger data-testid="staff-addon-method-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{m.replace('_', ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Payment Reference (optional)</Label>
              <Input data-testid="staff-addon-reference-input" value={referenceNumber} onChange={(e) => setReferenceNumber(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Notes (optional)</Label>
              <Textarea data-testid="staff-addon-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>

            <div className="rounded-xl bg-bone p-3 flex justify-between text-sm font-semibold">
              <span>Amount Due</span>
              <span data-testid="staff-addon-amount">${total.toFixed(2)}</span>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            data-testid="staff-addon-confirm-btn"
            disabled={!selected.length || busy}
            onClick={confirm}
            className="bg-saffron hover:bg-saffron-hover"
          >
            {busy ? 'Processing…' : 'Charge & Add Add-on'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
